import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  CircularProgress, TextField, Chip, InputAdornment,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, FormControl, InputLabel, Select, MenuItem,
  useTheme, useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';

const API = process.env.REACT_APP_API_URL || '';

const estiloTabla = {
  bgcolor: 'var(--c-superficie)',
  '& .MuiTableCell-root': { color: '#e0e0e0', borderColor: 'var(--c-primario)' }
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#e0e0e0',
    bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'var(--c-primario)' },
    '&:hover fieldset': { borderColor: 'var(--c-acento)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--c-acento)' },
  },
  '& .MuiInputLabel-root': { color: 'var(--c-acento)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--c-acento)' },
  '& .MuiSelect-icon': { color: 'var(--c-acento)' },
};

const labelStyle = { style: { color: 'var(--c-acento)' }, shrink: true };
const inputStyle = { style: { color: '#e0e0e0' } };
const menuProps = { PaperProps: { sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } } };

const FORM_VACIO = {
  nombre: '', apellido: '', dni: '', email: '', telefono: '',
  fecha_nacimiento: '', direccion: '', obra_social: '', numero_afiliado: ''
};

export default function Pacientes() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pacientes, setPacientes] = useState([]);
  const [obrasSociales, setObrasSociales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    cargar();
    axios.get(`${API}/api/obras-sociales`).then(r => setObrasSociales(r.data)).catch(() => {});
  }, []);

  async function cargar(termino = '') {
    setLoading(true);
    try {
      const params = termino ? `?buscar=${encodeURIComponent(termino)}` : '';
      const r = await axios.get(`${API}/api/pacientes${params}`);
      setPacientes(r.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function handleBuscar(e) {
    const val = e.target.value;
    setBuscar(val);
    if (val.length === 0 || val.length >= 3) cargar(val);
  }

  function abrirNuevo() {
    setEditando(null);
    setForm(FORM_VACIO);
    setError('');
    setDialogOpen(true);
  }

  function abrirEditar(p) {
    setEditando(p);
    setForm({
      nombre: p.nombre || '',
      apellido: p.apellido || '',
      dni: p.dni || '',
      email: p.email || '',
      telefono: p.telefono || '',
      fecha_nacimiento: p.fecha_nacimiento ? String(p.fecha_nacimiento).split('T')[0] : '',
      direccion: p.direccion || '',
      obra_social: p.obra_social || '',
      numero_afiliado: p.numero_afiliado || '',
    });
    setError('');
    setDialogOpen(true);
  }

  async function guardar() {
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setGuardando(true);
    setError('');
    try {
      if (editando) {
        await axios.put(`${API}/api/pacientes/${editando.id}`, form);
      } else {
        await axios.post(`${API}/api/pacientes`, form);
      }
      setDialogOpen(false);
      cargar(buscar);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  async function eliminar(p) {
    if (!window.confirm(`¿Eliminar al paciente ${p.nombre} ${p.apellido}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/api/pacientes/${p.id}`);
      cargar(buscar);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  }

  function formatFecha(f) {
    if (!f) return '—';
    return String(f).split('T')[0].split('-').reverse().join('/');
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
          Pacientes
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            size="small"
            placeholder="Buscar por nombre, DNI o email..."
            value={buscar}
            onChange={handleBuscar}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'var(--c-acento)', fontSize: 18 }} /></InputAdornment>,
              style: { color: '#e0e0e0' }
            }}
            sx={{ width: { xs: '100%', sm: 300 }, ...fieldSx }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' }, whiteSpace: 'nowrap' }}>
            Nuevo paciente
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress sx={{ color: 'var(--c-acento)' }} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
        <TableContainer component={Paper} sx={estiloTabla}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'var(--c-primario)' }}>
                <TableCell>Nombre</TableCell>
                <TableCell>DNI</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Fecha Nac.</TableCell>
                <TableCell>Obra Social</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pacientes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: '#b0b0b0', py: 4 }}>
                    {buscar ? 'Sin resultados para esa búsqueda.' : 'No hay pacientes registrados.'}
                  </TableCell>
                </TableRow>
              ) : pacientes.map(p => (
                <TableRow key={p.id} sx={{ '&:hover': { bgcolor: 'var(--c-primario)' } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{p.nombre} {p.apellido}</TableCell>
                  <TableCell>{p.dni || '—'}</TableCell>
                  <TableCell>{p.email || '—'}</TableCell>
                  <TableCell>{p.telefono || '—'}</TableCell>
                  <TableCell>{formatFecha(p.fecha_nacimiento)}</TableCell>
                  <TableCell>
                    {p.obra_social
                      ? <>{p.obra_social}{p.numero_afiliado ? ` · ${p.numero_afiliado}` : ''}</>
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {p.pre_registrado
                      ? <Chip label="Pre-registrado" size="small" sx={{ bgcolor: '#1a3a5c', color: 'var(--c-acento)', fontSize: 11 }} />
                      : <Chip label="Activo" size="small" color="success" sx={{ fontSize: 11 }} />
                    }
                  </TableCell>
                  <TableCell align="center">
                    {['admin', 'medico'].includes(localStorage.getItem('rol')) && (
                      <IconButton onClick={() => window.open(`/historial/${p.id}`, '_blank')} sx={{ color: '#81c784' }} size="small" title="Abrir Historia Clínica en nueva pestaña">
                        <MedicalInformationIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton onClick={() => abrirEditar(p)} sx={{ color: 'var(--c-acento)' }} size="small" title="Editar">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {localStorage.getItem('rol') === 'admin' && (
                      <IconButton onClick={() => eliminar(p)} sx={{ color: '#f48fb1' }} size="small" title="Eliminar">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ color: 'var(--c-acento)', fontWeight: 700 }}>
          {editando ? 'Editar paciente' : 'Nuevo paciente'}
          {editando && (
            <Typography sx={{ fontSize: 12, color: '#90a4ae', mt: 0.3 }}>ID: {editando.id}</Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Nombre *" fullWidth value={form.nombre}
                onChange={e => setForm({ ...form, nombre: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Apellido" fullWidth value={form.apellido}
                onChange={e => setForm({ ...form, apellido: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="DNI" fullWidth value={form.dni}
                onChange={e => setForm({ ...form, dni: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Email" type="email" fullWidth value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Teléfono" fullWidth value={form.telefono}
                onChange={e => setForm({ ...form, telefono: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
              <TextField label="Fecha de nacimiento" type="date" fullWidth value={form.fecha_nacimiento}
                onChange={e => setForm({ ...form, fecha_nacimiento: e.target.value })}
                InputLabelProps={{ shrink: true, style: { color: 'var(--c-acento)', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
                inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' } }} sx={fieldSx} />
            </Box>

            <TextField label="Dirección" fullWidth value={form.direccion}
              onChange={e => setForm({ ...form, direccion: e.target.value })}
              InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth sx={fieldSx}>
                <InputLabel>Obra social</InputLabel>
                <Select value={form.obra_social} label="Obra social"
                  onChange={e => setForm({ ...form, obra_social: e.target.value })}
                  MenuProps={menuProps}>
                  <MenuItem value=""><em style={{ color: '#888' }}>Sin especificar</em></MenuItem>
                  {obrasSociales.map(os => (
                    <MenuItem key={os.id} value={os.nombre}>{os.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label="N° afiliado" fullWidth value={form.numero_afiliado}
                onChange={e => setForm({ ...form, numero_afiliado: e.target.value })}
                InputLabelProps={labelStyle} inputProps={inputStyle} sx={fieldSx} />
            </Box>

          </Box>
          {error && <Typography sx={{ color: '#f44336', fontSize: 13, mt: 1.5 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={guardando}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
            {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear paciente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
